import {
  NavigationModalHeader_default
} from "./chunk-54FS4CKX.js";
import {
  PreventUnmounting_default
} from "./chunk-PT5DIVZ3.js";
import {
  HelperButton_default
} from "./chunk-2WY5CRLU.js";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BBZEL7EG.js";

// src/NavigationModalDialog.tsx
import { useModal } from "@levana/utils/modal";
import Box from "@mui/joy/Box";
import ModalDialog from "@mui/joy/ModalDialog";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import { useTheme } from "@mui/joy/styles";
import { styled, useThemeProps } from "@mui/joy/styles";
import useSlot from "@mui/joy/utils/useSlot";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Fragment, forwardRef, useCallback } from "react";
import { Fragment as Fragment2, jsx, jsxs } from "react/jsx-runtime";
var name = "LevanaNavigationModalDialog";
var NavigationModalDialogRoot = styled(ModalDialog, {
  name,
  slot: "root"
})(
  ({ theme, ownerState }) => ({
    "--ModalContent-horizontalPadding": `max(
      var(--ModalContent-padding), 
      calc((100vw - var(--ModalContent-maxWidth)) / 2)
    )`,
    [theme.breakpoints.up(ownerState.centerBreakpoint)]: {
      "--ModalContent-horizontalPadding": "calc((var(--ModalDialog-maxWidth) - var(--ModalContent-maxWidth)) / 2)"
    }
  })
);
var NavigationModalDialogHeader = styled(NavigationModalHeader_default, {
  name,
  slot: "header"
})({});
var NavigationModalDialogContent = styled(Box, {
  name,
  slot: "content"
})({
  marginTop: "var(--NavigationModal-contentTop, 0.5rem)"
});
var NavigationModalDialog = forwardRef(function NavigationModalDialog2(inProps, ref) {
  const props = useThemeProps({ props: inProps, name });
  const _a = props, {
    rootId,
    topItem,
    stack,
    setStack,
    onDismiss,
    centerBreakpoint = "sm",
    children
  } = _a, otherProps = __objRest(_a, [
    "rootId",
    "topItem",
    "stack",
    "setStack",
    "onDismiss",
    "centerBreakpoint",
    "children"
  ]);
  const ownerState = __spreadProps(__spreadValues({}, props), { centerBreakpoint });
  const externalForwardedProps = __spreadValues({}, otherProps);
  const [SlotRoot, rootProps] = useSlot("root", {
    ref,
    className: "root",
    elementType: NavigationModalDialogRoot,
    ownerState,
    externalForwardedProps
  });
  const [SlotHeader, headerProps] = useSlot("header", {
    ref,
    className: "header",
    elementType: NavigationModalDialogHeader,
    ownerState,
    externalForwardedProps
  });
  const [SlotContent, contentProps] = useSlot("content", {
    ref,
    className: "content",
    elementType: NavigationModalDialogContent,
    ownerState,
    externalForwardedProps
  });
  const { dismiss } = useModal();
  const handlePop = useCallback(() => {
    setStack((stack2) => stack2.length > 1 ? stack2.slice(0, -1) : stack2);
  }, [setStack]);
  const handleNavigationBarPop = (topItem == null ? void 0 : topItem.canPop) && stack.length > 1 ? handlePop : void 0;
  const handlePush = useCallback(
    (next) => {
      setStack((stack2) => {
        if (typeof next === "string") {
          return [...stack2, { type: "dynamic", id: next }];
        } else {
          return [...stack2, __spreadValues({ type: "static" }, next)];
        }
      });
    },
    [setStack]
  );
  const handleDismiss = () => {
    var _a2;
    dismiss();
    (_a2 = props.onDismiss) == null ? void 0 : _a2.call(props);
  };
  const theme = useTheme();
  const isCenterBreakpoint = useMediaQuery(
    theme.breakpoints.up(centerBreakpoint)
  );
  return /* @__PURE__ */ jsxs(
    SlotRoot,
    __spreadProps(__spreadValues({}, rootProps), {
      size: "lg",
      layout: isCenterBreakpoint ? "center" : "fullscreen",
      children: [
        /* @__PURE__ */ jsxs(
          SlotHeader,
          __spreadProps(__spreadValues({}, headerProps), {
            onBack: handleNavigationBarPop,
            canClose: topItem == null ? void 0 : topItem.canClose,
            children: [
              (topItem == null ? void 0 : topItem.subtitle) && /* @__PURE__ */ jsx(
                Typography,
                {
                  level: "body-xs",
                  sx: {
                    textTransform: "uppercase",
                    height: "var(--ModalHeader-height, calc(var(--IconButton-size) * 0.66))"
                  },
                  children: topItem.subtitle
                }
              ),
              /* @__PURE__ */ jsxs(ModalTitle, { children: [
                (topItem == null ? void 0 : topItem.icon) && /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", mb: 1 }, children: typeof topItem.icon === "function" ? /* @__PURE__ */ jsx(topItem.icon, { sx: { color: "text.secondary" } }) : /* @__PURE__ */ jsx(Fragment2, { children: topItem.icon }) }),
                (topItem == null ? void 0 : topItem.helper) ? /* @__PURE__ */ jsxs(
                  Stack,
                  {
                    direction: "row",
                    columnGap: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    children: [
                      topItem.title,
                      /* @__PURE__ */ jsx(
                        HelperButton_default,
                        __spreadProps(__spreadValues({}, topItem.helper), {
                          "aria-label": `modal ${topItem.title} helper`
                        })
                      )
                    ]
                  }
                ) : topItem == null ? void 0 : topItem.title
              ] })
            ]
          })
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              width: "100%",
              alignSelf: "center",
              overflow: "auto",
              px: "var(--ModalContent-horizontalPadding)",
              pb: "var(--ModalContent-padding)"
            },
            children: /* @__PURE__ */ jsxs(SlotContent, __spreadProps(__spreadValues({}, contentProps), { children: [
              stack.map((item) => /* @__PURE__ */ jsx(Fragment, { children: item.type === "static" && /* @__PURE__ */ jsx(PreventUnmounting_default, { show: (topItem == null ? void 0 : topItem.id) === item.id, children: /* @__PURE__ */ jsx(
                item.view,
                {
                  push: handlePush,
                  pop: handlePop,
                  close: handleDismiss
                }
              ) }) }, item.id)),
              typeof children === "function" ? children({
                push: handlePush,
                pop: handlePop,
                close: handleDismiss
              }) : children
            ] }))
          }
        )
      ]
    })
  );
});
var ModalTitle = (props) => {
  const _a = props, { sx, children } = _a, joyProps = __objRest(_a, ["sx", "children"]);
  return /* @__PURE__ */ jsx(
    Typography,
    __spreadProps(__spreadValues({}, joyProps), {
      level: "h3",
      sx: __spreadProps(__spreadValues({}, sx), {
        textAlign: "center"
      }),
      children
    })
  );
};
var NavigationModalDialog_default = NavigationModalDialog;

export {
  NavigationModalDialog_default
};
